import * as React from 'react';
import * as _ from "lodash";
import * as ReactDOM from 'react-dom';
import { connect } from "react-redux";
import { bindActionCreators } from 'redux';
import { RootState } from '../reducers';
import { LogDetail, IpLogDetail } from '../reducers/logsReducer';
import { fetchLogs, fetchIpLogs, updateDefaultNumberOfRecords } from "../actions/logsActions";
import { ReactComponent as Success } from "../assets/icon_success.svg"
import { ReactComponent as Failure } from "../assets/icon_failure.svg"
import { ReactComponent as Deferred } from "../assets/icon_deferred.svg"
import { ReactComponent as Bounced } from "../assets/icon_bounced.svg"
import { ReactComponent as ArrowLeft } from "../assets/arrow_left.svg"
import { ReactComponent as ArrowRight } from "../assets/arrow_right.svg"
import { CopyButton } from "./CopyButton"
import Dropdown, { DropdownOption } from "./Dropdown";
import { ReactComponent as ScrollTop } from "../assets/scroll_top.svg"
import { capitalize } from './utils';
import { FormattedMessage } from "react-intl";

interface LogDetailsProps {
    fetchLogs: (queryParams: any, token: string) => any,
    fetchIpLogs: (queryParams: any, token: string) => any,
    updateDefaultNumberOfRecords: (noOfRecords: number) => void,
    fetchingLogs: boolean,
    details: LogDetail[] | IpLogDetail[],
    token: string,
    totalRecords: number,
    senderMailSubmitted: string,
    receiverMailSubmitted: string,
    ipSubmitted: string,
    timestamp: number,
    status: string,
    direction: string,
    recordsPerPage: number,
    page: number,
    defaultNoOfRecords: number
}

interface LogDetailsState {
    numberOfRecordsVisible: number,
    currentPage: number
}

const recordsPerPageDropdownItems = [
    {
        label: '5',
        value: 5
    },
    {
        label: '10',
        value: 10
    },
    {
        label: '20',
        value: 20
    }
]

interface PropsParent {
    scrollableDivRef: React.RefObject<any>
}

type Props = LogDetailsProps & PropsParent

class LogDetails extends React.PureComponent<Props> {
    state = {
        isScrollToTopVisible: false,
        isMailLogSelected: (this.props.details[0] && (this.props.details[0] as LogDetail).from) ? true : false,
        numberOfRecordsVisible: this.props.defaultNoOfRecords,
        currentPage: 1
    }
    topDivRef: React.RefObject<HTMLDivElement>
    scrollableDiv: HTMLDivElement

    constructor(props: Props) {
        super(props)
        this.getStatusLogo = this.getStatusLogo.bind(this);
        this.toggleVisibility = this.toggleVisibility.bind(this)
        this.handleScrollToTopClick = this.handleScrollToTopClick.bind(this)
        this.getMailLogDetails = this.getMailLogDetails.bind(this)
        this.getIpLogDetails = this.getIpLogDetails.bind(this)
        this.topDivRef = React.createRef();
        this.getPagedListForLogs = this.getPagedListForLogs.bind(this)
        this.scrollableDiv = this.props.scrollableDivRef.current
    }

    componentDidMount() {
        this.scrollableDiv.addEventListener('scroll', _.debounce(e => {
            this.toggleVisibility()
        }, 500, { trailing: true, leading: true }))
    }

    toggleVisibility() {
        if (this.scrollableDiv.scrollTop > 0) {
            this.setState({
                isScrollToTopVisible: true
            })
        } else {
            this.setState({
                isScrollToTopVisible: false
            })
        }
    }

    handleScrollToTopClick() {
        if (this.scrollableDiv) {
            this.scrollableDiv.scrollTop = 0
            this.setState({
                isScrollToTopVisible: false
            })
        }
    }

    getStatusLogo = (status: string) => {
        status = capitalize(status)
        let iconComponent
        switch (status) {
            case "Success":
                iconComponent = <Success />
                break;
            case "Failure":
                iconComponent = <Failure />
                break;
            case "Deferred":
                iconComponent = <Deferred />
                break;
            case "Bounced":
                iconComponent = <Bounced />
                break;
        }
        return <div>{iconComponent}<span className="log-details-status-text"> {status} </span></div>
    }

    copyToClipboard = (item: any) => {
        const el = document.createElement('textarea');
        el.value = JSON.stringify(item);
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }



    loadPreviousPage = () => {
        if (this.props.fetchingLogs || this.state.currentPage === 1) return
        this.setState({
            currentPage: this.state.currentPage - 1
        })
    }

    loadNextPage = () => {
        if (this.props.fetchingLogs) return
        const recordsShown = this.state.numberOfRecordsVisible * this.state.currentPage
        if (recordsShown < this.props.totalRecords) {
            const shouldFetchNextPage = recordsShown + this.state.numberOfRecordsVisible > this.props.details.length &&
                recordsShown + this.state.numberOfRecordsVisible < this.props.totalRecords
            if (shouldFetchNextPage) {
                if (this.state.isMailLogSelected) {
                    this.props.fetchLogs(this.getFetchLogsQueryParams(this.props.page + 1), this.props.token);
                }
                else {
                    this.props.fetchIpLogs(this.getFetchIpLogsQueryParams(this.props.page + 1), this.props.token);
                }
            }
            this.setState({
                currentPage: this.state.currentPage + 1
            })
        }
    }

    getFetchLogsQueryParams = (page: number) => {
        const fetchLogsQueryParams = {
            "from": this.props.senderMailSubmitted.trim(),
            "to": this.props.receiverMailSubmitted.trim(),
            "timestamp": this.props.timestamp,
            "status": this.props.status,
            "direction": this.props.direction,
            "records_per_page": this.props.recordsPerPage,
            "page": page
        }
        return fetchLogsQueryParams
    }

    getFetchIpLogsQueryParams = (page: number) => {
        const fetchLogsQueryParams = {
            "ip": this.props.ipSubmitted,
            "records_per_page": this.props.recordsPerPage,
            "page": page
        }
        return fetchLogsQueryParams
    }


    updateRecordsPerPage = (item: DropdownOption) => {
        if (this.props.fetchingLogs || item.value === this.state.numberOfRecordsVisible) return
        this.props.updateDefaultNumberOfRecords(item.value)
        this.setState({
            currentPage: 1,
            numberOfRecordsVisible: item.value
        })
    }

    getPagedListForLogs = () => {
        const startIdx = (this.state.currentPage - 1) * this.state.numberOfRecordsVisible
        const endIdx = Math.min(this.state.currentPage * this.state.numberOfRecordsVisible, this.props.totalRecords)
        return (this.props.details).slice(startIdx, endIdx)
    }

    getIpLogDetails = () => {
        const pagedLogs = this.getPagedListForLogs() as IpLogDetail[]
        return (pagedLogs).map((item: IpLogDetail) => {
            let columnTag = []
            columnTag.push(<td className="log-details-time">{item.dateTime}</td>)
            columnTag.push(<td>{item.ip}</td>)
            columnTag.push(<td>{item.reason}</td>)
            columnTag.push(<td className="log-details-info">{item.info}</td>)
            columnTag.push(<td className="copy-button">
                <CopyButton onClick={() => this.copyToClipboard(item)} /></td>)
            return <tr>{columnTag}</tr>
        })
    }

    getMailLogDetails = (showFromColumn: boolean, showToColumn: boolean) => {
        const pagedLogs = this.getPagedListForLogs() as LogDetail[]
        return (pagedLogs).map((item: LogDetail) => {
            let columnTag = []
            columnTag.push(<td className="log-details-time">{item.dateTime}</td>)
            { if (showFromColumn) columnTag.push(<td className="log-details-email">{item.from}</td>) }
            { if (showToColumn) columnTag.push(<td className="log-details-email">{item.to}</td>) }
            columnTag.push(<td>{capitalize(item.direction)}</td>)
            columnTag.push(<td className="log-details-status">{this.getStatusLogo(item.status)}</td>)
            columnTag.push(<td className="log-details-info">{item.info}</td>)
            columnTag.push(<td className="copy-button">
                <CopyButton onClick={() => this.copyToClipboard(item)} /></td>)
            return <tr>{columnTag}</tr>
        })
    }

    render() {
        if (this.props.fetchingLogs) {
            return (<div className="log-loader"></div>)
        }
        const shouldShowFromColumn = !this.props.senderMailSubmitted
        const shouldShowToColumn = !this.props.receiverMailSubmitted


        const detailsRows = this.state.isMailLogSelected ? this.getMailLogDetails(shouldShowFromColumn, shouldShowToColumn) : this.getIpLogDetails()
        const mailLogHeaderRow = <tr>
            <th className="log-details-time-heading"><FormattedMessage id='heading_date_time' /></th>
            {shouldShowFromColumn ? <th className="log-details-email">
                <FormattedMessage id='from' /></th> : ""}
            {shouldShowToColumn ? <th className="log-details-email">
                <FormattedMessage id='to' /></th> : ""}
            <th><FormattedMessage id='direction' /></th>
            <th className="log-details-status"><FormattedMessage id='status' /></th>
            <th className="log-details-info"><FormattedMessage id='info' /></th>
            <th className="copy-icon"></th> {/* This is needed to for the copy icon */}
        </tr>

        const ipLogHeaderRow = <tr>
            <th className="log-details-time-heading"><FormattedMessage id='heading_date_time' /></th>
            <th><FormattedMessage id='client_ip' /></th>
            <th><FormattedMessage id='reason' /></th>
            <th className="log-details-info"><FormattedMessage id='info' /></th>
            <th className="copy-icon"></th> {/* This is needed to for the copy icon */}
        </tr>
        const headerRow = this.state.isMailLogSelected ? mailLogHeaderRow : ipLogHeaderRow
        const totalPages = Math.ceil(this.props.totalRecords / this.state.numberOfRecordsVisible)
        const itemSelectedIndex = recordsPerPageDropdownItems.findIndex(dropDownOption => dropDownOption.value === this.state.numberOfRecordsVisible)
        return (
            <div>
                <div ref={this.topDivRef} className="log-details-container" >
                    <table className="log-details">
                        {headerRow}
                        {detailsRows}
                    </table>
                </div >
                {this.state.isScrollToTopVisible ? <ScrollTop className="scroll-to-top-button" onClick={this.handleScrollToTopClick} ></ScrollTop> : ""}
                <div className="page-div">
                    <text className="page-text"><FormattedMessage id='label_records_per_page' /></text>
                    <Dropdown
                        mainClass="records-div"
                        headerClass="records-dd-header"
                        headerTitleClass="records-dd-header-title"
                        listContainerClass="records-dd-list-container"
                        dropDownItemClass="records-dd-menu-item"
                        onItemClick={this.updateRecordsPerPage}
                        placeholderText=""
                        placeholderClass=""
                        list={recordsPerPageDropdownItems}
                        defaultItemSelectedIndexes={[itemSelectedIndex]}
                        shouldAllowMultiSelect={false}
                    />
                    <text className="page-text">{this.state.currentPage} of {totalPages}</text>
                    <div className="page-buttons">
                        <ArrowLeft className="prev-page" onClick={() => this.loadPreviousPage()} />
                        <ArrowRight className="next-page" onClick={() => this.loadNextPage()} />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        token: state.auth.token,
        details: state.logs.fetchLogsPayload,
        totalRecords: state.logs.totalRecords,
        senderMailSubmitted: state.logs.senderMailSubmitted,
        receiverMailSubmitted: state.logs.receiverMailSubmitted,
        ipSubmitted: state.logs.ipSubmitted,
        timestamp: state.logs.timestamp,
        status: state.logs.status,
        direction: state.logs.direction,
        recordsPerPage: state.logs.recordsPerPage,
        page: state.logs.page,
        fetchingLogs: state.logs.fetchingLogs,
        defaultNoOfRecords: state.logs.defaultNoOfRecords
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        ...bindActionCreators({ fetchLogs, fetchIpLogs, updateDefaultNumberOfRecords }, dispatch)
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(LogDetails);